import { TZDate } from '@date-fns/tz';
import type { BookingProposalRecord } from '@navio/nsdk';
import { CustomDateFormats, NoddiDateFormats, noddiFormatDate, stringToUpperCase } from 'noddi-util';
import type { NormalizedCapacitySlot } from 'noddi-web';
import { useStationaryCapacityQuery } from 'noddi-web';
import { useMemo, useState } from 'react';
import { getSuggestionWindow, isOpenDay, pickSuggestedSlots } from '../utils/suggestedProposalTimes';

export type SuggestedProposalTime = {
  slot: NormalizedCapacitySlot;
  /** "Tuesday 16. September" — weekday capitalized, since the tile starts a line. */
  dateLabel: string;
  /** "08:00 – 10:00". */
  timeLabel: string;
};

type Props = {
  proposal: BookingProposalRecord | undefined;
  garageId: number | undefined;
  salesItemIds: number[] | undefined;
  enabled: boolean;
};

/**
 * The times a Trønderdekk stationary proposal opens on — at most three, fewer when
 * capacity is thin, never padded. Capacity is read from the open-day window in
 * `suggestedProposalTimes` (never today, never a Sunday) and narrowed by the seeded
 * strategy there. A suggested date replaces the lead, and one time on that day is
 * always included so it is the first recommendation. Nothing is held here; the bay
 * is only reserved when the customer confirms.
 */
export const useSuggestedProposalTimes = ({ proposal, garageId, salesItemIds, enabled }: Props) => {
  // Pin the window to mount so a midnight rollover cannot re-pick times mid-session.
  const [mountTime] = useState(() => Date.now());

  const suggestionWindow = getSuggestionWindow(mountTime, proposal?.dateSuggested);
  const dateFrom = noddiFormatDate(suggestionWindow.start, NoddiDateFormats.API_DATE);
  const dateTo = noddiFormatDate(suggestionWindow.end, NoddiDateFormats.API_DATE);
  const pinDayKey = suggestionWindow.anchored
    ? noddiFormatDate(suggestionWindow.start, CustomDateFormats.NO_DASHED_DATE_ISO_8601_SHORT)
    : undefined;

  const isEnabled = enabled && garageId != null && (salesItemIds?.length ?? 0) > 0;

  const { slots, timeZone, isPending, error } = useStationaryCapacityQuery({
    garageId,
    dateFrom,
    dateTo,
    salesItemIds,
    enabled: isEnabled,
    labelMode: 'start-end',
    // Nothing is held while the customer reads, so the trio must reflect live capacity.
    keepFresh: true
  });

  const proposalId = proposal?.id;

  const suggestions = useMemo((): SuggestedProposalTime[] => {
    if (proposalId == null) {
      return [];
    }

    const toZoned = (value: string) => (timeZone ? new TZDate(new Date(value), timeZone) : new Date(value));
    // The range spans a Sunday; drop those rather than suggest a day the garage is closed.
    const dated = slots
      .map((slot) => ({ slot, zonedStart: toZoned(slot.start), id: slot.id }))
      .filter(({ zonedStart }) => isOpenDay(zonedStart))
      .map(({ slot, zonedStart, id }) => ({
        slot,
        id,
        dayKey: noddiFormatDate(zonedStart, CustomDateFormats.NO_DASHED_DATE_ISO_8601_SHORT)
      }));

    return pickSuggestedSlots(dated, { seed: proposalId, pinDayKey }).map(({ slot }) => ({
      slot,
      dateLabel: stringToUpperCase(noddiFormatDate(toZoned(slot.start), CustomDateFormats.FULL_WEEKDAY_DAY_DOT_MONTH)),
      timeLabel: slot.label
    }));
  }, [slots, timeZone, proposalId, pinDayKey]);

  return {
    suggestions,
    isPending: isEnabled && isPending,
    error
  };
};
